import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { jsPDF } from "jspdf";
// If using the external XLSX library, ensure the import is adjusted to your usage
import XLSX from "../utils/xlsx";

function CreateReportModal({ text, ClientID, allAssets, isMobile, cancelReport }) {
  // ---------------------------
  //  State
  // ---------------------------
  const [originalReports, setOriginalReports] = useState([]);
  const [reportNames, setReportNames] = useState([]);
  const [agingReports, setAgingReports] = useState([]);
  const [assets, setAssets] = useState([
    "Hydrants",
    "Manholes",
    "TrafficSigns",
    "Valves",
    "WaterMeters",
    "WaterLines",
    "SewerLines",
  ]);

  const [selectedAsset, setSelectedAsset] = useState("Hydrants");
  const [reportCreated, setReportCreated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reportErrors, setReportErrors] = useState("");

  // local references for the chart data URIs
  const [chartDataUis, setChartDataUis] = useState([]);

  // ---------------------------
  //  Effects
  // ---------------------------
  useEffect(() => {
    // On mount, fetch available reports
    let isMounted = true;
    setIsLoading(true);
    axios
      .get("/api/getreports")
      .then((res) => {
        if (!isMounted) return;
        const original = res.data.asset.reportArray; // e.g. array of possible reports
        const selected = original.filter((x) => x.name.includes(selectedAsset));

        setOriginalReports(original);
        setReportNames(selected);
        setAgingReports(res.data.aging.reportArray);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching reports:", err);
        setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [selectedAsset]);

  // ---------------------------
  //  Handlers / Helpers
  // ---------------------------

  /**
   * Called when picking a certain asset's chart
   */
  const onSelectAsset = (asset) => {
    console.log('onSelectAsset', asset);
    setReportErrors("");
    // e.g. "Hydrants", "Manholes"
    const newReports = originalReports.filter((x) => x.name.includes(asset));
    setSelectedAsset(asset);
    setReportNames(newReports);
    // Then display the reports after a small delay, passing the asset and reports directly
    setTimeout(() => displayReport(asset, newReports), 1000);
  };

  /**
   * Generate a spreadsheet (CSV) for an asset's inventory
   */
  const onSelectAssetSpreadsheet = async (assetPlural) => {
    // e.g. assetPlural = "Hydrants"
    setReportErrors("");
    try {
      const singularAsset = assetPlural.slice(0, -1); // "Hydrant"
      // fetch fields for that asset
      const { data } = await axios.get(`/api/assetFields/${singularAsset}`);
      const assetFields = data.asset;
      if (!assetFields || !assetFields.inputObjectArray) {
        setReportErrors("No fields found for the selected report.");
        return;
      }

      // filter the relevant allAssets by assetType
      const relevantAssets = allAssets.filter((x) => x.assetType === singularAsset);

      // build columns from displayed fields
      const displayedFields = assetFields.inputObjectArray.filter((x) => x.display);
      const displayColumns = displayedFields.map((x) => x.displayName);
      const fieldNames = displayedFields.map((x) => x.name);

      // also add "LastInspectionDate"
      displayColumns.push("LastInspectionDate");
      fieldNames.push("LastInspectionDate");

      // build the CSV rows
      const sheetRows = [];
      // first row is the headings
      sheetRows.push(displayColumns);

      // subsequent rows with data
      for (const item of relevantAssets) {
        const row = [];
        for (const fieldName of fieldNames) {
          const val = item[fieldName];
          if (!val) {
            row.push("");
          } else if (fieldName.includes("Date") && typeof val === "string") {
            // e.g. "2023-01-02T00:00:00.000Z"
            row.push(val.split("T")[0]);
          } else {
            row.push(val);
          }
        }
        sheetRows.push(row);
      }

      if (sheetRows.length === 1) {
        setReportErrors("No data found for the selected report");
        return;
      }

      // Convert to CSV
      const csvContent = sheetRows.map((row) => row.join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${singularAsset}Data.csv`;
      link.click();
    } catch (err) {
      console.error("Error generating asset spreadsheet:", err);
      setReportErrors("Error generating asset spreadsheet.");
    }
  };

  /**
   * Generate a CSV from the given report names (for "Aging" or custom)
   */
  const onSelectCSVReportSpreadsheet = async (assetReports) => {
    // e.g. array of report names
    setIsLoading(true);
    setReportErrors("");

    try {
      const { data } = await axios.post("/api/runreport", {
        ClientID,
        reportNames: assetReports,
      });
      // data.chart is presumably an object with keys = each reportName, value = array of rows
      const chartData = data.chart;
      if (!chartData) {
        setReportErrors("No data found for the selected report");
        setIsLoading(false);
        return;
      }

      const reportDataErrorArray = [];
      // for each report
      for (let i = 0; i < assetReports.length; i++) {
        const repName = assetReports[i];
        const rows = chartData[repName];
        if (!rows || !rows.length) {
          reportDataErrorArray.push(1); // mark an error for that report
          continue;
        }
        // Build CSV
        const rowKeys = Object.keys(rows[0]);
        // Add keys as first row
        const sheetRows = [rowKeys];

        // subsequent rows
        for (const rowItem of rows) {
          const row = [];
          for (const k of rowKeys) {
            let val = rowItem[k];
            if (!val) {
              row.push("");
            } else if (k.toLowerCase().includes("date") && typeof val === "string") {
              row.push(val.split("T")[0]);
            } else {
              row.push(val);
            }
          }
          // check LastInspectionDate if it’s in the row
          if (rowItem.LastInspectionDate) {
            const newStr = rowItem.LastInspectionDate.split("T")[0];
            row.push(newStr || "");
          }
          sheetRows.push(row);
        }

        const csvContent = sheetRows.map((r) => r.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${repName}Data.csv`;
        link.click();

        reportDataErrorArray.push(0);
      }

      // if none had data
      if (reportDataErrorArray.every((r) => r === 1)) {
        setReportErrors("No data found for the selected report");
      }
    } catch (err) {
      console.error("Error generating CSV from reports:", err);
      setReportErrors("No data found for the selected report");
    }
    setIsLoading(false);
  };

  /**
   * Display the selected "reportNames" for the chosen asset in a new tab (charts)
   */
  const displayReport = async (asset = selectedAsset, reports = reportNames) => {
    if (!reports.length) {
      setReportErrors("No reports for the selected asset");
      return;
    }
    setIsLoading(true);
    setReportCreated(false);
    setReportErrors("");

    try {
      const res = await axios.post("/api/runreport", {
        ClientID,
        reportNames: reports.map((x) => x.name),
      });
      // success => open charts in new tab
      localStorage.setItem("chartOptions", JSON.stringify(buildChartOptions(res.data)));
      localStorage.setItem("newReports", JSON.stringify(reports));
      localStorage.setItem("reportTitle", `${asset} Inventory Report`);
      console.log('reportTitle', `${asset} Inventory Report`);
      console.log('reports being used:', reports);
      window.open("/charts", "_blank");
      setIsLoading(false);
      setReportCreated(true);
    } catch (err) {
      console.error("Error displaying report:", err);
      setReportErrors("Failed to run the selected report(s).");
      setIsLoading(false);
    }
  };

  /**
   * Display performance metrics in a new tab
   */
  const displayPerformanceMetrics = async () => {
    setIsLoading(true);
    setReportCreated(false);
    setReportErrors("");

    try {
      // Find the Performance Metrics object from agingReports
      const performanceMetricsObj = agingReports.find(report => 
        report.displayName === "Performance Metrics"
      );

      if (!performanceMetricsObj || !performanceMetricsObj.sps) {
        setReportErrors("Performance Metrics not available");
        setIsLoading(false);
        return;
      }

      const res = await axios.post("/api/runPerformanceMetrics", {
        ClientID,
        performanceMetrics: performanceMetricsObj.sps,
      });

      // Store data for the performance metrics page
      localStorage.setItem("performanceMetricsData", JSON.stringify(res.data.metrics));
      localStorage.setItem("performanceChartOptions", JSON.stringify(buildPerformanceChartOptions(res.data.metrics)));
      localStorage.setItem("performanceMetricsTitle", "Performance Metrics Report");
      
      // Store metadata for display names
      if (performanceMetricsObj && performanceMetricsObj.sps) {
        localStorage.setItem("performanceMetricsMetadata", JSON.stringify(performanceMetricsObj.sps));
      }
      
      // Open performance metrics page in new tab
      window.open("/performance-metrics", "_blank");
      setIsLoading(false);
      setReportCreated(true);
    } catch (err) {
      console.error("Error displaying performance metrics:", err);
      setReportErrors("Failed to run performance metrics.");
      setIsLoading(false);
    }
  };

  /**
   * Build "chartOptions" from the runreport result
   */
  const buildChartOptions = (runReportData) => {
    // runReportData.chart has shape: { reportName: [{someKey, someVal}, ...], ...}
    const resultObj = runReportData.chart;
    if (!resultObj) return [];

    const chartOptions = [];
    const reportResultNames = Object.keys(resultObj);
    for (let i = 0; i < reportResultNames.length; i++) {
      const key = reportResultNames[i];
      const arr = resultObj[key];
      if (!arr || !arr.length) continue;

      // assume arr[0] has keys like [someKey, category, value]
      const objKeys = Object.keys(arr[0]);
      // e.g. dataValues is arr of the second key, dataValuesCounts is arr of the third key
      const dataValues = arr.map((x) => (x[objKeys[1]] === "" ? "Not Reported" : x[objKeys[1]]));
      const dataValuesCounts = arr.map((x) => (x[objKeys[2]] === "" ? "Not Reported" : x[objKeys[2]]));

      // format the name with spacing
      const readableName = key.match(/[A-Z][a-z]+/g).join(" ") || key;

      const oneChart = {
        options: {
          chart: { type: "bar", width: "100%", height: "100%" },
          series: [{ name: readableName, data: dataValuesCounts }],
          xaxis: { categories: dataValues },
          title: {
            text: readableName,
            align: "left",
            margin: 10,
            offsetX: 0,
            offsetY: 0,
            floating: false,
            style: { fontSize: "14px", fontWeight: "bold", color: "#263238" },
          },
        },
        series: [{ name: readableName, data: dataValuesCounts }],
        id: `chart${i}`,
      };
      chartOptions.push(oneChart);
    }
    return chartOptions;
  };

  /**
   * Build chart options for performance metrics
   */
  const buildPerformanceChartOptions = (metricsData) => {
    const chartOptions = [];
    const metricNames = Object.keys(metricsData);
    
    for (let i = 0; i < metricNames.length; i++) {
      const metricName = metricNames[i];
      const data = metricsData[metricName];
      
      if (!data || !data.length) continue;

      // Get the display name from the performance metrics object
      const performanceMetricsObj = agingReports.find(report => 
        report.displayName === "Performance Metrics"
      );
      
      let displayName = metricName;
      if (performanceMetricsObj && performanceMetricsObj.sps) {
        const metricObj = performanceMetricsObj.sps.find(sp => sp.name === metricName);
        if (metricObj && metricObj.displayName) {
          displayName = metricObj.displayName;
        }
      }

      // For AssetCountByUserSEL, create a chart showing user performance
      if (metricName === 'KPI.AssetCountByUserSEL') {
        const users = data.map(row => row.UserName || 'Unknown User');
        const totalAssets = data.map(row => {
          const hydrantCount = parseInt(row.HydrantRecordsCreated || 0) + parseInt(row.HydrantRecordsEdited || 0);
          const manholeCount = parseInt(row.ManholeRecordsCreated || 0) + parseInt(row.ManholeRecordsEdited || 0);
          const trafficSignCount = parseInt(row.TrafficSignRecordsCreated || 0) + parseInt(row.TrafficSignRecordsEdited || 0);
          const sewerLineCount = parseInt(row.SewerLineRecordsCreated || 0) + parseInt(row.SewerLineRecordsEdited || 0);
          const valveCount = parseInt(row.ValveRecordsCreated || 0) + parseInt(row.ValveRecordsEdited || 0);
          const waterLineCount = parseInt(row.WaterLineRecordsCreated || 0) + parseInt(row.WaterLineRecordsEdited || 0);
          const waterMeterCount = parseInt(row.WaterMeterRecordsCreated || 0) + parseInt(row.WaterMeterRecordsEdited || 0);
          
          return hydrantCount + manholeCount + trafficSignCount + sewerLineCount + valveCount + waterLineCount + waterMeterCount;
        });

        const chartOption = {
          options: {
            chart: { type: "bar", width: "100%", height: "100%" },
            series: [{ name: "Total Asset Records", data: totalAssets }],
            xaxis: { categories: users },
            title: {
              text: displayName || "User Asset Record Counts",
              align: "left",
              margin: 10,
              offsetX: 0,
              offsetY: 0,
              floating: false,
              style: { fontSize: "14px", fontWeight: "bold", color: "#263238" },
            },
          },
          series: [{ name: "Total Asset Records", data: totalAssets }],
          id: `performanceChart${i}`,
        };
        chartOptions.push(chartOption);
      }
      
      // For CallsPerClientPreviousXDaysSEL, create a simple metric display
      if (metricName === 'KPI.CallsPerClientPreviousXDaysSEL') {
        const callCount = data[0]?.NumCalls || 0;
        const chartOption = {
          options: {
            chart: { type: "bar", width: "100%", height: "100%" },
            series: [{ name: "Calls", data: [callCount] }],
            xaxis: { categories: ["Last 30 Days"] },
            title: {
              text: displayName || "Calls Per Client (Last 30 Days)",
              align: "left",
              margin: 10,
              offsetX: 0,
              offsetY: 0,
              floating: false,
              style: { fontSize: "14px", fontWeight: "bold", color: "#263238" },
            },
          },
          series: [{ name: "Calls", data: [callCount] }],
          id: `performanceChart${i}`,
        };
        chartOptions.push(chartOption);
      }
      
      // For any other metric types, create a generic chart
      if (!chartOptions.some(chart => chart.id === `performanceChart${i}`)) {
        // Try to create a generic chart based on the data structure
        const firstRow = data[0];
        if (firstRow) {
          const numericColumns = Object.keys(firstRow).filter(key => {
            const value = firstRow[key];
            return typeof value === 'number' || !isNaN(parseFloat(value));
          });
          
          if (numericColumns.length > 0) {
            const chartOption = {
              options: {
                chart: { type: "bar", width: "100%", height: "100%" },
                series: [{ name: displayName, data: data.map(row => parseFloat(row[numericColumns[0]]) || 0) }],
                xaxis: { categories: data.map((_, index) => `Record ${index + 1}`) },
                title: {
                  text: displayName,
                  align: "left",
                  margin: 10,
                  offsetX: 0,
                  offsetY: 0,
                  floating: false,
                  style: { fontSize: "14px", fontWeight: "bold", color: "#263238" },
                },
              },
              series: [{ name: displayName, data: data.map(row => parseFloat(row[numericColumns[0]]) || 0) }],
              id: `performanceChart${i}`,
            };
            chartOptions.push(chartOption);
          }
        }
      }
    }
    
    return chartOptions;
  };

  // not used in your final code but left as an example
  const saveDiv = (divId, title) => {
    // If you want to generate a PDF from your chart images
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.setFont(undefined, "bold");

    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth = doc.getTextWidth(`${selectedAsset} Inventory Report`);
    const textX = (pageWidth - textWidth) / 2;
    doc.text(`${selectedAsset} Inventory Report`, textX, 25);

    // your logic to place images...
    // doc.addImage(...) for each chartDataUis item
    // doc.save(...)
  };

  // UI for each asset in the left column (Charts)
  const renderAssetButtons = () => {
    return (
      <div style={isMobile ? {} : { display: "flex", justifyContent: "space-between" }}>
        {/* Column 1: Chart Buttons */}
        <div style={{ flex: 1 }}>
          <h2 className="containerHeader" style={{ padding: "16px 0px", fontSize: "14px" }}>
            Inventory - Charts
          </h2>
          {assets.map((obj) => {
            const mostSimilarIcon = findClosestIcon(obj);
            return (
              <div key={obj} style={{ margin: "8px" }}>
                <Button
                  variant="contained"
                  style={{ padding: "10px 0px 10px 12px", width: "170px", display: "flex", alignItems: "center" }}
                  onClick={() => onSelectAsset(obj)}
                >
                  <img
                    src={require(`../../markerIcons/${mostSimilarIcon}`)}
                    className={obj.includes("Manhole") ? "leaflet-svg-icon-sewer" : ""}
                    style={{ width: "20px", height: "20px", marginRight: "10px" }}
                    alt={obj}
                  />
                  <span style={{ margin: "auto" }}>
                    <b>{obj}</b>
                  </span>
                </Button>
              </div>
            );
          })}
        </div>

        {/* Column 2: Spreadsheet Buttons */}
        <div style={{ flex: 1 }}>
          <h2 className="containerHeader" style={{ padding: "16px 0px", fontSize: "14px" }}>
            Inventory - Spreadsheets
          </h2>
          {assets.map((obj) => {
            const mostSimilarIcon = findClosestIcon(obj);
            return (
              <div key={`${obj}Spreadsheet`} style={{ margin: "8px" }}>
                <Button
                  variant="contained"
                  style={{ padding: "10px 0px 10px 12px", width: "170px", display: "flex", alignItems: "center" }}
                  onClick={() => onSelectAssetSpreadsheet(obj)}
                >
                  <img
                    src={require(`../../markerIcons/${mostSimilarIcon}`)}
                    className={obj.includes("Manhole") ? "leaflet-svg-icon-sewer" : ""}
                    style={{ width: "20px", height: "20px", marginRight: "10px" }}
                    alt={obj}
                  />
                  <span style={{ margin: "auto" }}>
                    <b>{obj}</b>
                  </span>
                </Button>
              </div>
            );
          })}
        </div>

        {/* Column 3: Aging Reports */}
        <div style={{ flex: 1 }}>
          <h2 className="containerHeader" style={{ padding: "16px 0px", fontSize: "14px" }}>
            Aging - List
          </h2>
          {agingReports.filter(report => report.displayName !== "Performance Metrics").map((obj) => (
            <div key={obj.displayName} style={{ margin: "8px" }}>
              <Button
                variant="contained"
                style={{ padding: "10px 12px", display: "flex", width: "max-content", minWidth: "-webkit-fill-available" }}
                onClick={() => onSelectCSVReportSpreadsheet(obj.names)}
              >
                <span>
                  <b>{obj.displayName}</b>
                </span>
              </Button>
            </div>
          ))}
        </div>

        {/* Column 4: Performance Metrics */}
        <div style={{ flex: 1 }}>
          <h2 className="containerHeader" style={{ padding: "16px 0px", fontSize: "14px" }}>
            Performance Metrics
          </h2>
          {agingReports.find(report => report.displayName === "Performance Metrics") && (
            <div style={{ margin: "8px" }}>
              <Button
                variant="contained"
                style={{ padding: "10px 12px", display: "flex", width: "max-content", minWidth: "-webkit-fill-available" }}
                onClick={displayPerformanceMetrics}
              >
                <span>
                  <b>Performance Metrics</b>
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * Finds the "closest" icon for an asset name
   * (You had a string similarity approach, but for brevity we'll just do partial match.)
   */
  const findClosestIcon = (assetName) => {
    const fileIconArray = [
      "hydrant.svg",
      "manhole.svg",
      "trafficSign.svg",
      "watermeter.svg",
      "valve.svg",
      "waterline.svg",
      "sewerline.svg",
    ];
    // remove trailing "s" for simpler matching => "Hydrant", "WaterLine"
    const baseName = assetName.endsWith("s") ? assetName.slice(0, -1) : assetName;
    const lowerBase = baseName.toLowerCase();

    // find a match in fileIconArray
    const found = fileIconArray.find((icon) => {
      const lowerIcon = icon.toLowerCase();
      return lowerIcon.includes(lowerBase) || lowerBase.includes(lowerIcon);
    });
    return found || "trafficSign.svg";
  };

  // ---------------------------
  //  Render
  // ---------------------------
  return (
    <div
      style={{
        background: "white",
        width: "fit-content",
        padding: "35px",
        height: "auto",
        margin: "4vw auto",
        borderRadius: "10px",
        position: "relative",
      }}
    >
      <h3 style={{ textAlign: "center" }}>{isLoading ? "Generating Report..." : text}</h3>
      <div style={{ display: "flex", justifyContent: "evenly" }}>
        <div style={{ padding: "10px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
          {isLoading ? (
            <div>
              <CircularProgress />
            </div>
          ) : (
            renderAssetButtons()
          )}
        </div>
      </div>

      {reportErrors && (
        <div style={{ color: "red", textAlign: "center" }}>{reportErrors}</div>
      )}
      <br />
      <div style={{ display: "flex", justifyContent: "space-around" }}>
        <Button variant="contained" color="secondary" onClick={cancelReport}>
          Close
        </Button>
      </div>
    </div>
  );
}

export default CreateReportModal;
